import { A, pipe } from "@mobily/ts-belt"
import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditPaste } from "@shared/models/analytics/lesson"
import { getPossessiveForLesson } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { CardStack, CardStackAttributes } from "@shared/models/card-stack"
import type { Planbook } from "@shared/models/planbook"
import { findCourseDateCustom } from "cc-frontend/lib/find-course-date"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import { compact, map, partial } from "lodash-es"
import moment from "moment"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultLessonModels from "../../lesson/default-lesson-models"
import defaultPlanbookLessonsActivity from "./default-planbook-lessons-activity"
import defaultPlanbookLessonsContext from "./default-planbook-lessons-context"

type CardStacksForCourses = {
  courseId: string
  oldCardStack: CardStack
  oldCardStackId: string
  newCardStackAttrs: CardStackAttributes
}
interface Payload {
  cardStacksForCourses: Array<CardStacksForCourses>
  dateString: string
  planbook: Planbook
  firstEditPatches: Array<PatchModel>
  firstEditUndoPatches: Array<PatchModel>
  ownerIds: Array<string>
  planbookId: string
}

export function preparePasteAllLessonsForDay(payload: Payload): PreparedAction {
  return {
    name: PLANBOOK_PASTE_ALL_LESSONS_FOR_DAY.name,
    payload: payload,
    patches: PLANBOOK_PASTE_ALL_LESSONS_FOR_DAY.patches(payload),
    undoPatches: PLANBOOK_PASTE_ALL_LESSONS_FOR_DAY.undoPatches(payload),
    narrative: partial(PLANBOOK_PASTE_ALL_LESSONS_FOR_DAY.narrative, payload),
  }
}

export const PLANBOOK_PASTE_ALL_LESSONS_FOR_DAY = {
  name: "PLANBOOK_PASTE_ALL_LESSONS_FOR_DAY",

  patches(payload: Payload): Array<PatchModel> {
    let patches = compact(
      map(payload.cardStacksForCourses, (hash) => {
        if (hash.oldCardStackId) {
          let newAttrs = hash.newCardStackAttrs
          let patch = Patch.create("card-stack", hash.oldCardStackId)
          patch
            .set("attributes.schemaVersion", newAttrs.schemaVersion)
            .set("attributes.cards", newAttrs.cards)
            .set("attributes.title", newAttrs.title)
          if (newAttrs.schemaVersion === 2) {
            patch
              .set("attributes.proseMirror", newAttrs.proseMirror)
              .set("attributes.extracted", newAttrs.extracted)
          }
          return patch.patch
        }

        return null
      })
    )
    let firstEditPatches = payload.firstEditPatches || []

    return firstEditPatches.concat(patches)
  },

  undoPatches(payload: Payload): Array<PatchModel> {
    let patches = compact(
      map(payload.cardStacksForCourses, (hash) => {
        if (hash.oldCardStack) {
          let oldAttrs = hash.oldCardStack.attributes
          let patch = Patch.create("card-stack", hash.oldCardStack.id)
          patch
            .set("attributes.schemaVersion", oldAttrs.schemaVersion)
            .set("attributes.cards", oldAttrs.cards)
            .set("attributes.title", oldAttrs.title)
          if (oldAttrs.schemaVersion === 2) {
            patch
              .set("attributes.proseMirror", oldAttrs.proseMirror)
              .set("attributes.extracted", oldAttrs.extracted)
          }
          return patch.patch
        }

        return null
      })
    )

    let firstEditUndoPatches = payload.firstEditUndoPatches || []

    return firstEditUndoPatches.concat(patches)
  },

  narrative(payload: Payload, findFn: FindFn, userId: string): Narrative | null {
    let planbook = findFn("planbook", payload.planbook.id)
    let formattedDate = moment(payload.dateString).format("ddd, MMM. D, YYYY")
    let analyticsEvents = pipe(
      payload.cardStacksForCourses,
      A.filter((cardStack) => {
        let courseDate = findCourseDateCustom(
          findFn("course", cardStack.courseId),
          payload.dateString
        )
        return (
          courseDate?.attributes.cardStackId !== undefined &&
          courseDate.attributes.cardStackId !== null
        )
      }),
      A.map((cardStack): LessonEditPaste | null => {
        let courseDate = findCourseDateCustom(
          findFn("course", cardStack.courseId),
          payload.dateString
        )
        let cardStackId = courseDate?.attributes.cardStackId
        // We should never hit this because of the filter above ^
        if (cardStackId === undefined || cardStackId === null) return null
        let models = defaultLessonModels(
          { cardStackId: cardStackId, planbookId: payload.planbookId },
          findFn,
          userId
        )
        return {
          ...createBlankEvent(),
          root_id: payload.planbookId,
          root_type: "planbook",
          date_plan_scheduled_for: payload.dateString,
          primary_object_id: cardStackId,
          primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
          primary_verb: "edit",
          primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbookId),
          primary_object_type: "lesson",
          primary_object_specifier: null,

          adverbial_verb: "pasting",
          adverbial_object_type: "lesson",
          adverbial_object_specifier: "single_lesson",
          adverbial_object_id: cardStackId,
          adverbial_possessive: getPossessiveForLesson(
            findFn,
            userId,
            payload.ownerIds || []
          ),
          adverbial_object_id_and_ancestor_ids: [payload.planbookId],
        }
      }),
      (arr) => compact(arr)
    )

    let context = defaultPlanbookLessonsContext(planbook, payload)
    context.editType = "paste"

    return {
      title: "Pasted an entire day of lessons",
      titleForAnalytics: "Lesson - Paste Entire Day",
      description: `Pasted a day's worth of lessons on ${formattedDate}`,
      context: context,
      activity: defaultPlanbookLessonsActivity(payload.planbook.id, userId),
      newAnalyticsEvents: analyticsEvents,
    }
  },
}
export default PLANBOOK_PASTE_ALL_LESSONS_FOR_DAY
